import { useState, useEffect, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import api from '../../services/api';
import { clientAuthConfig } from '../../utils/clientAuthConfig';

function formatTime(value) {
  const d = new Date(value);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function MessagesPanel({ client, agentSlug, onUnreadCount }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const agentFirst = client?.agent?.first_name || 'your agent';

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/clients/me/messages', clientAuthConfig(agentSlug));
      const next = res.data.messages || [];
      // Only autoscroll when a message actually arrived — a poll tick must not
      // yank the client back to the bottom while they're reading up-thread.
      setMessages((prev) => {
        if (next.length > prev.length) scrollToBottom();
        return next;
      });
      // Reflects how many were unread at fetch time — the same request marks
      // them read server-side, so this is a one-time "you had N" signal for a
      // parent badge, not a live unread count.
      onUnreadCount?.(res.data.unread_count || 0);
    } catch {
      setError('Could not load messages.');
    } finally {
      setLoading(false);
    }
  }, [scrollToBottom, agentSlug, onUnreadCount]);

  useEffect(() => { load(); }, [load]);

  // A client waiting on a reply with the tab open otherwise sees nothing
  // appear without a manual reload — poll, and refetch when the tab regains focus.
  useEffect(() => {
    const interval = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const send = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await api.post('/clients/me/messages', { content }, clientAuthConfig(agentSlug));
      setMessages((m) => [...m, res.data.message]);
      setDraft('');
      scrollToBottom();
    } catch {
      setError('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <section className="client-portal__panel">
      <h3 className="client-portal__panel-title">Message {agentFirst}</h3>
      <p className="client-portal__panel-desc">
        Ask {agentFirst} anything — progress on your search or sale, next steps, or a quick question. {agentFirst} replies here personally.
      </p>

      <div className="client-messages__thread">
        {loading ? (
          <div className="client-messages__loading">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="client-messages__empty">
            No messages yet. Say hello or ask {agentFirst} a question.
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role === 'client';
            return (
              <div key={m.id} className={`client-messages__row${mine ? ' client-messages__row--mine' : ''}`}>
                <div className="client-messages__bubble-wrap">
                  <div className="client-messages__bubble">
                    {m.content}
                  </div>
                  <div className="client-messages__meta">
                    {mine ? 'You' : agentFirst} · {formatTime(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {error && <div className="client-messages__error">{error}</div>}

      <div className="client-messages__composer">
        <textarea
          className="client-messages__input"
          placeholder={`Message ${agentFirst}…`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
        />
        <button
          type="button"
          className="client-messages__send"
          onClick={send}
          disabled={sending || !draft.trim()}
        >
          <Send size={16} /> Send
        </button>
      </div>
    </section>
  );
}

export default MessagesPanel;
